import { useState, useEffect } from 'react';
import { CalendarIcon, X } from 'lucide-react';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

const PRESETS = [
    { id: 'today',     label: 'Today',     getDates: () => { const t = new Date(); return [t, t]; } },
    { id: 'yesterday', label: 'Yesterday', getDates: () => { const y = new Date(); y.setDate(y.getDate() - 1); return [y, y]; } },
    { id: '7d',        label: 'Last 7d',   getDates: () => { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 6); return [s, e]; } },
    { id: '30d',       label: 'Last 30d',  getDates: () => { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 29); return [s, e]; } },
    { id: 'mtd',       label: 'This month', getDates: () => { const t = new Date(); return [new Date(t.getFullYear(), t.getMonth(), 1), t]; } },
];

const toISO = (d) => {
    if (!d) return null;
    const tz = d.getTimezoneOffset();
    return new Date(d.getTime() - tz * 60_000).toISOString().slice(0, 10);
};

const fmt = (d) => d ? d.toLocaleDateString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric' }) : '';

/**
 * Date picker that supports single-day OR range selection.
 *
 * Props:
 *   value: { start: Date|null, end: Date|null }
 *   onChange: ({ start, end, startIso, endIso, mode }) => void
 *   testId: string (optional)
 */
export const ReportDateRangePicker = ({ value, onChange, testId = 'report-date-picker' }) => {
    const [mode, setMode] = useState(() => (value?.start && value?.end && +value.start === +value.end ? 'single' : 'range'));
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState(() => ({ start: value?.start || null, end: value?.end || null }));

    useEffect(() => {
        setDraft({ start: value?.start || null, end: value?.end || null });
    }, [value?.start, value?.end]);

    const apply = (start, end) => {
        const s = start || null;
        const e = (mode === 'single' ? start : end) || null;
        onChange?.({
            start: s,
            end: e,
            startIso: toISO(s),
            endIso: toISO(e),
            mode,
        });
        setOpen(false);
    };

    const applyPreset = (preset) => {
        const [s, e] = preset.getDates();
        const isSingle = +s === +e;
        setMode(isSingle ? 'single' : 'range');
        setDraft({ start: s, end: e });
        onChange?.({ start: s, end: e, startIso: toISO(s), endIso: toISO(e), mode: isSingle ? 'single' : 'range' });
        setOpen(false);
    };

    const clear = () => {
        setDraft({ start: null, end: null });
        onChange?.({ start: null, end: null, startIso: null, endIso: null, mode });
    };

    const label = (() => {
        if (!value?.start) return 'Pick a date';
        if (mode === 'single' || !value.end || +value.start === +value.end) return fmt(value.start);
        return `${fmt(value.start)} → ${fmt(value.end)}`;
    })();

    return (
        <div className="inline-flex items-center gap-1" data-testid={testId}>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        className="bg-black/40 border-white/10 text-white hover:bg-white/5 font-mono text-xs h-9"
                        data-testid={`${testId}-trigger`}
                    >
                        <CalendarIcon className="w-3.5 h-3.5 mr-1.5 text-[#FFD700]" />
                        {label}
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    align="end"
                    className="w-auto p-0 bg-[#0a0a0a] border-white/10 text-white"
                    data-testid={`${testId}-popover`}
                >
                    <div className="p-3 border-b border-white/10 space-y-2">
                        <div className="flex items-center gap-1 bg-black/30 rounded-md p-1">
                            <button
                                type="button"
                                onClick={() => { setMode('single'); setDraft((d) => ({ start: d.start, end: d.start })); }}
                                className={`flex-1 px-3 py-1 rounded text-xs font-bold transition-colors ${
                                    mode === 'single' ? 'bg-[#FFD700] text-black' : 'text-[#A0AAB2] hover:text-white'
                                }`}
                                data-testid={`${testId}-mode-single`}
                            >
                                Single day
                            </button>
                            <button
                                type="button"
                                onClick={() => setMode('range')}
                                className={`flex-1 px-3 py-1 rounded text-xs font-bold transition-colors ${
                                    mode === 'range' ? 'bg-[#FFD700] text-black' : 'text-[#A0AAB2] hover:text-white'
                                }`}
                                data-testid={`${testId}-mode-range`}
                            >
                                Date range
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-1">
                            {PRESETS.map((p) => (
                                <button
                                    type="button"
                                    key={p.id}
                                    onClick={() => applyPreset(p)}
                                    className="px-2 py-1 rounded-full text-[10px] font-bold bg-white/5 border border-white/10 text-[#A0AAB2] hover:text-white hover:bg-white/10"
                                    data-testid={`${testId}-preset-${p.id}`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    {mode === 'single' ? (
                        <Calendar
                            mode="single"
                            selected={draft.start}
                            onSelect={(d) => {
                                setDraft({ start: d, end: d });
                                if (d) apply(d, d);
                            }}
                            initialFocus
                        />
                    ) : (
                        <Calendar
                            mode="range"
                            selected={{ from: draft.start, to: draft.end }}
                            onSelect={(r) => {
                                const s = r?.from || null;
                                const e = r?.to || null;
                                setDraft({ start: s, end: e });
                                if (s && e) apply(s, e);
                            }}
                            numberOfMonths={2}
                            initialFocus
                        />
                    )}
                </PopoverContent>
            </Popover>
            {value?.start && (
                <button
                    type="button"
                    onClick={clear}
                    className="p-1.5 rounded-md border border-white/10 text-[#A0AAB2] hover:text-white hover:bg-white/5"
                    title="Clear"
                    data-testid={`${testId}-clear`}
                >
                    <X className="w-3.5 h-3.5" />
                </button>
            )}
        </div>
    );
};

export default ReportDateRangePicker;
